import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { deviceIds } from 'src/embross-device-manager';
import {
  applyBoardingPassStatusCode,
  parseBoardingPassStatusIsOK,
  toBoardingPassStatusCode,
} from '@app/cuss/embross/utils/callbacks/boardingpass.callback';
import { CussStatusCode } from '@app/enums/cuss-device-status.enum';
import { DEVICE, NEW_RELIC_EVENT } from '@app/enums/new-relic.enum';
import { CussService } from '@app/services/cuss.service';
import { NewRelicEventsService } from '@app/services/new-relic-events.service';
import { BoardingPassLowPaperModalComponent } from '@modals/boardingpass-low-paper-modal/boardingpass-low-paper-modal.component';
import { BoardingPassPaperEmptyModalComponent } from '@modals/boardingpass-paper-empty-modal/boardingpass-paper-empty-modal.component';

/** Condición física del papel de la impresora de pases de abordar. */
export type BoardingPassPaperCondition = 'normal' | 'low' | 'empty';

/** Métrica y código CUSS asociados a cada condición que se reporta. */
const CONDITION_TELEMETRY: Record<
  Exclude<BoardingPassPaperCondition, 'normal'>,
  { event: string; statusCode: number }
> = {
  low: {
    event: NEW_RELIC_EVENT.ATB_PRINTER_MEDIA_LOW,
    statusCode: CussStatusCode.MEDIA_LOW,
  },
  empty: {
    event: NEW_RELIC_EVENT.ATB_PRINTER_MEDIA_EMPTY,
    statusCode: CussStatusCode.MEDIA_EMPTY,
  },
};

/**
 * Clases de la ventana del bloqueo de papel vacío. Reutiliza el encuadre
 * fullscreen ya existente (`colibri-fullscreen-modal`) y añade una clase propia
 * para no compartir selectores con las variantes `network` / `emergency`.
 */
const EMPTY_BLOCKER_WINDOW_CLASS = 'colibri-fullscreen-modal boardingpass-paper-empty-modal-window';

/** Superficie mínima del `Device` del SDK usada para la consulta formal de estado. */
interface AtbPrinterDeviceLike {
  statusIsOK?: (returnPromise: boolean) => Promise<unknown> | null;
  getLastDeviceStatus?: () => unknown;
}

/**
 * Única fuente de verdad, en memoria, del estado de papel de la impresora de
 * pases de abordar (ATB Printer) — CI-23161.
 *
 * Modela DOS ejes deliberadamente separados:
 *
 * 1. `condition` — la condición FÍSICA reportada por el hardware:
 *    `normal` (0 OK), `low` (107 MEDIA_LOW) o `empty` (108 MEDIA_EMPTY).
 *    Solo cambia cuando el hardware reporta otro código; los duplicados de
 *    `statusChange` y del sondeo periódico `statusIsOK` son inertes.
 *
 * 2. `lowShownForCurrentHomeVisit` — la PRESENTACIÓN del aviso de papel bajo.
 *    Se reinicia en CADA entrada a Home, de modo que el aviso vuelve a aparecer
 *    después de cada flujo mientras el papel siga bajo, pero nunca se duplica
 *    dentro de una misma estancia en Home.
 *
 * Esta separación es la diferencia esencial contra `BagtagMediaLowService`, que
 * presenta una sola vez por episodio: aquí NO se reutiliza esa semántica.
 *
 * El papel vacío tiene prioridad sobre el papel bajo: retira el aviso de papel
 * bajo propio y levanta una superficie bloqueante a pantalla completa que impide
 * iniciar flujos nuevos sin cancelar, limpiar ni navegar fuera del flujo que ya
 * estuviera en curso.
 *
 * No hay persistencia de ningún tipo (localStorage / sessionStorage / IndexedDB /
 * backend). Tras un reinicio de la app el estado se pierde y Home vuelve a
 * consultar el estado real del dispositivo mediante la API pública del SDK.
 *
 * Este servicio NO se conecta al dispositivo. La recepción de eventos vive en
 * `boardingpass.callback.ts`, asignado por `CussService` (standby) y por
 * `ATBPrinterDeviceService.enable()` (activo); ambos delegan aquí mediante
 * `notifyMediaLow()` / `notifyMediaEmpty()` / `notifyMediaRecovered()`.
 */
@Injectable({
  providedIn: 'root',
})
export class BoardingPassPaperService implements OnDestroy {
  /** Condición física vigente del papel. Arranca en `normal`. */
  private condition: BoardingPassPaperCondition = 'normal';

  /** Home está montada y visible. */
  private homeVisible = false;

  /** El aviso de papel bajo ya se presentó durante la estancia actual en Home. */
  private lowShownForCurrentHomeVisit = false;

  /** Única referencia al modal informativo de papel bajo. */
  private lowModalRef: NgbModalRef | null = null;

  /** Única referencia a la superficie bloqueante de papel vacío. */
  private emptyModalRef: NgbModalRef | null = null;

  /** Espera —sin polling— a que se cierren otros modales para presentar el aviso. */
  private activeInstancesSub: Subscription | null = null;

  /**
   * Rutina con la que la pantalla visible retira SUS PROPIAS capas de carga.
   *
   * No es un flujo reactivo ni un bus de eventos: es una sola ranura opcional,
   * invocada en un único momento (al pasar a `empty`). Existe porque el dueño del
   * modal de carga es `LoadingService`, pero el CONTEO y las banderas auxiliares
   * de cada capa viven en la pantalla que la creó; cerrar el modal desde aquí
   * dejaría esos espejos desincronizados. Ver `setHostLoadingRelease`.
   */
  private releaseHostLoading: (() => void) | null = null;

  constructor(
    private readonly cussService: CussService,
    private readonly ngbModal: NgbModal,
    private readonly zone: NgZone,
    private readonly newRelicEvents: NewRelicEventsService
  ) {}

  // ─── Estado observable (solo lectura, para consumidores y pruebas) ───────────

  /** Condición física vigente del papel. */
  get currentCondition(): BoardingPassPaperCondition {
    return this.condition;
  }

  /**
   * El kiosco está inhabilitado por falta de papel: ningún flujo nuevo debe
   * iniciarse mientras esto sea `true`.
   *
   * Se deriva de la condición física —no de que el modal haya podido abrirse—
   * para que un fallo al renderizar la superficie no habilite el kiosco.
   */
  get isKioskBlocked(): boolean {
    return this.condition === 'empty';
  }

  /** El aviso de papel bajo está abierto en este momento. */
  get isLowModalOpen(): boolean {
    return this.lowModalRef !== null;
  }

  /**
   * Home no debe iniciar un flujo nuevo, ni por toque ni por lectura de hardware.
   *
   * Son dos causas distintas y deliberadamente acotadas:
   * - papel vacío: el kiosco está inhabilitado hasta que el papel se reponga;
   * - aviso de papel bajo visible: el pasajero aún no lo ha reconocido con
   *   `Continuar`, y el modal solo bloquea el toque, no los escáneres.
   *
   * La condición `low` por sí sola NO bloquea: tras pulsar `Continuar` el papel
   * sigue bajo y el flujo debe poder iniciarse con normalidad.
   */
  get isFlowStartBlocked(): boolean {
    return this.isKioskBlocked || this.isLowModalOpen;
  }

  /** La superficie bloqueante de papel vacío está abierta en este momento. */
  get isEmptyBlockerOpen(): boolean {
    return this.emptyModalRef !== null;
  }

  /** El aviso de papel bajo ya se presentó en la estancia actual en Home. */
  get wasLowShownForCurrentHomeVisit(): boolean {
    return this.lowShownForCurrentHomeVisit;
  }

  /**
   * Registra la rutina con la que la pantalla visible retira sus propias capas de
   * `FULL_LOADING`; `null` la da de baja.
   *
   * Cierra este hueco: una lectura que ya había mostrado la capa de carga queda a
   * medias cuando llega `108`, el lector no vuelve a emitir y, al recuperarse el
   * papel, la capa aparece sobre una Home sin nada que la retire. El guard de la
   * pantalla sólo se evalúa con el evento SIGUIENTE, que puede no existir.
   *
   * Se resuelve con una llamada de vuelta en lugar de cerrar el modal desde aquí
   * porque `LoadingService` cuenta capas: dismiss directo dejaría su `loadingCount`
   * y los espejos de la pantalla (`homeFullLoadingDepth`, el acuse de lectura en
   * curso y el cierre diferido) apuntando a una capa que ya no existe, y la
   * siguiente lectura legítima se mostraría mal. La pantalla ya sabe hacerlo bien.
   */
  setHostLoadingRelease(release: (() => void) | null): void {
    this.releaseHostLoading = release;
  }

  ngOnDestroy(): void {
    this.teardownActiveInstancesWatch();
  }

  // ─── Entradas desde los callbacks del dispositivo ────────────────────────────

  /**
   * `107 MEDIA_LOW` recibido por cualquiera de los dos callbacks (activo o
   * compartido) o por la consulta formal de estado.
   */
  notifyMediaLow(): void {
    this.applyCondition('low');
  }

  /**
   * `108 MEDIA_EMPTY` recibido por cualquiera de los dos callbacks o por la
   * consulta formal. Tiene prioridad sobre el papel bajo.
   */
  notifyMediaEmpty(): void {
    this.applyCondition('empty');
  }

  /** `0 OK` recibido por cualquiera de los dos callbacks o por la consulta formal. */
  notifyMediaRecovered(): void {
    this.applyCondition('normal');
  }

  /**
   * Consulta formal del estado real del dispositivo.
   *
   * Usa `statusIsOK(true)` (Promise del SDK) y, si no está disponible, recurre a
   * `getLastDeviceStatus()`. Un rechazo, timeout, ausencia de dispositivo o
   * respuesta inválida es INERTE: no fabrica una condición, no limpia una
   * conocida, no abre ni cierra superficies y no propaga excepciones.
   *
   * Solo un `0` explícito y válido devuelve la máquina a `normal`. El fallback se
   * interpreta con `toBoardingPassStatusCode` justamente para que `null`,
   * `undefined`, `''` o un payload inválido NO se coercionen a `0`: de lo
   * contrario una consulta fallida retiraría un bloqueo de papel vacío real.
   */
  queryDeviceStatus(): void {
    const device = this.getDevice();
    if (!device) {
      return;
    }

    try {
      const promise = typeof device.statusIsOK === 'function' ? device.statusIsOK(true) : null;
      if (promise && typeof promise.then === 'function') {
        promise.then(
          (response: unknown) =>
            this.zone.run(() =>
              applyBoardingPassStatusCode(parseBoardingPassStatusIsOK(response), this)
            ),
          () => {
            /* Rechazo o timeout: sin efecto sobre el estado. */
          }
        );
        return;
      }
    } catch {
      /* Consulta rechazada: sin efecto sobre el estado. */
    }

    try {
      if (typeof device.getLastDeviceStatus === 'function') {
        applyBoardingPassStatusCode(toBoardingPassStatusCode(device.getLastDeviceStatus()), this);
      }
    } catch {
      /* Sin efecto sobre el estado. */
    }
  }

  // ─── Señales de Home ─────────────────────────────────────────────────────────

  /**
   * Home quedó visible: arranca una nueva estancia. Se libera el acuse del aviso
   * de papel bajo, se reconcilia el estado real del dispositivo y se presenta el
   * aviso si el papel sigue bajo.
   */
  notifyHomeVisible(): void {
    this.homeVisible = true;
    this.lowShownForCurrentHomeVisit = false;
    this.queryDeviceStatus();
    this.presentLowIfPossible();
  }

  /**
   * Home dejó de estar visible (navegación hacia un flujo). Conserva la condición
   * física —el papel sigue como esté— y solo retira el aviso propio de Home.
   * La superficie bloqueante de papel vacío NO se toca: no depende de Home.
   */
  notifyHomeHidden(): void {
    this.homeVisible = false;
    this.teardownActiveInstancesWatch();
    this.closeLowModal();
  }

  // ─── Máquina de estados de la condición física ───────────────────────────────

  /**
   * Aplica la condición reportada por el hardware.
   *
   * La idempotencia es por TRANSICIÓN: recibir el mismo código otra vez —por un
   * `statusChange` repetido o por el sondeo `statusIsOK` cada 30/60 s— no abre ni
   * reemplaza superficies, no emite métrica y no altera el acuse del aviso.
   */
  private applyCondition(next: BoardingPassPaperCondition): void {
    if (next === this.condition) {
      return;
    }
    this.condition = next;
    this.trackTransition(next);
    this.syncSurfaces();
  }

  /**
   * Alinea las superficies visibles con la condición física vigente.
   *
   * - `empty`  → retira el aviso de papel bajo propio y levanta el bloqueo.
   * - `low`    → retira el bloqueo y presenta el aviso si Home lo permite.
   * - `normal` → retira ambas superficies y libera el acuse de la presentación.
   */
  private syncSurfaces(): void {
    if (this.condition === 'empty') {
      this.teardownActiveInstancesWatch();
      this.closeLowModal();
      // Antes de tapar la pantalla: la capa de carga de una lectura a medias se
      // retira ya, no en el evento siguiente —que puede no llegar nunca—.
      this.releaseHostLoadingLayers();
      this.openEmptyBlocker();
      return;
    }

    this.closeEmptyBlocker();

    if (this.condition === 'low') {
      this.presentLowIfPossible();
      return;
    }

    this.teardownActiveInstancesWatch();
    this.closeLowModal();
    this.lowShownForCurrentHomeVisit = false;
  }

  // ─── Presentación del aviso de papel bajo ────────────────────────────────────

  /**
   * Presenta el aviso solo si el papel sigue bajo, Home está visible, aún no se
   * presentó en esta estancia y no hay otro modal interfiriendo. En cualquier
   * otro caso el aviso simplemente espera: nunca interrumpe un flujo activo.
   */
  private presentLowIfPossible(): void {
    if (this.condition !== 'low' || !this.homeVisible) {
      return;
    }
    if (this.lowShownForCurrentHomeVisit || this.lowModalRef) {
      return;
    }
    if (this.ngbModal.hasOpenModals()) {
      // Otro modal está abierto: se espera —sin polling— a que la pila quede libre.
      this.watchActiveInstances();
      return;
    }
    this.openLowModal();
  }

  /**
   * Abre la única instancia del aviso de papel bajo.
   *
   * Se envuelve en `NgZone.run` porque los callbacks CUSS llegan fuera de la zona
   * de Angular; al reentrar, la detección de cambios se dispara sola al vaciarse
   * la cola de microtareas.
   */
  private openLowModal(): void {
    this.zone.run(() => {
      try {
        this.lowModalRef = this.ngbModal.open(BoardingPassLowPaperModalComponent, {
          centered: true,
          size: 'lg',
          backdrop: 'static',
          keyboard: false,
        });
      } catch {
        // Si el modal no pudo abrirse, el acuse no se marca: se reintentará en la
        // próxima entrada a Home mientras el papel siga bajo.
        this.lowModalRef = null;
        return;
      }

      this.lowShownForCurrentHomeVisit = true;
      this.teardownActiveInstancesWatch();

      // Se ata al ref concreto: al sustituir la superficie (p. ej. LOW → EMPTY) la
      // promesa del modal anterior resuelve después y no debe borrar la nueva.
      //
      // El canal de la promesa distingue el acuse real del cierre ajeno: el CTA
      // `Continuar` llama `NgbActiveModal.close()` y RESUELVE; cualquier otro
      // cierre —`dismissAll()` de otra feature, un `dismiss` inesperado— RECHAZA.
      // El aviso no tiene botón de cerrar, así que no hay un tercer camino.
      const openedRef = this.lowModalRef;
      openedRef.result.then(
        () => this.onLowModalClosed(openedRef, true),
        () => this.onLowModalClosed(openedRef, false)
      );
    });
  }

  /** Cierra el aviso de papel bajo si está abierto. Idempotente. */
  private closeLowModal(): void {
    if (!this.lowModalRef) {
      return;
    }
    const ref = this.lowModalRef;
    this.lowModalRef = null;
    this.zone.run(() => {
      try {
        ref.dismiss();
      } catch {
        /* El modal ya estaba cerrado. */
      }
    });
  }

  /**
   * Resuelve el cierre del aviso de papel bajo distinguiendo TRES causas:
   *
   * 1. Cierre intencional de la feature (`closeLowModal`, por LOW → EMPTY, por
   *    recuperación o al salir de Home): suelta la referencia ANTES de
   *    `dismiss()`, de modo que aquí `lowModalRef !== ref` y no se hace nada.
   * 2. `Continuar` (`acknowledged`): es el único acto funcional de aceptación.
   *    El acuse de la estancia se conserva y el aviso no vuelve a aparecer hasta
   *    el próximo regreso a Home.
   * 3. Cierre externo/inesperado: NO es aceptación. Se libera el acuse y se
   *    reintenta la presentación, porque la obligación de informar sigue vigente.
   *
   * El caso 3 no genera un bucle: `presentLowIfPossible` exige `low` + Home y,
   * si quien cerró el aviso abrió otro modal, espera a que la pila se libere en
   * lugar de reabrir de inmediato.
   */
  private onLowModalClosed(ref: NgbModalRef, acknowledged: boolean): void {
    if (this.lowModalRef !== ref) {
      return;
    }
    this.lowModalRef = null;

    if (acknowledged) {
      return;
    }

    this.lowShownForCurrentHomeVisit = false;
    this.presentLowIfPossible();
  }

  // ─── Superficie bloqueante de papel vacío ────────────────────────────────────

  /**
   * Levanta la superficie bloqueante. No depende de Home y no navega: se
   * superpone a la pantalla actual, dejando el flujo subyacente intacto en
   * memoria para que reaparezca tal cual al recuperarse el papel.
   */
  private openEmptyBlocker(): void {
    if (this.emptyModalRef) {
      return;
    }
    this.zone.run(() => {
      try {
        this.emptyModalRef = this.ngbModal.open(BoardingPassPaperEmptyModalComponent, {
          size: 'full',
          backdrop: 'static',
          keyboard: false,
          windowClass: EMPTY_BLOCKER_WINDOW_CLASS,
        });
      } catch {
        // El kiosco sigue inhabilitado por condición aunque la superficie falle.
        this.emptyModalRef = null;
        return;
      }

      const openedRef = this.emptyModalRef;
      openedRef.result.then(
        () => this.onEmptyBlockerClosed(openedRef),
        () => this.onEmptyBlockerClosed(openedRef)
      );
    });
  }

  /** Retira la superficie bloqueante si está abierta. Idempotente. */
  private closeEmptyBlocker(): void {
    if (!this.emptyModalRef) {
      return;
    }
    const ref = this.emptyModalRef;
    this.emptyModalRef = null;
    this.zone.run(() => {
      try {
        ref.dismiss();
      } catch {
        /* La superficie ya estaba cerrada. */
      }
    });
  }

  /**
   * Resuelve el cierre de la superficie bloqueante distinguiendo la causa, que es
   * la diferencia crítica de esta HU:
   *
   * - Cierre INTENCIONAL de la feature (`closeEmptyBlocker`, única vía por la que
   *   el papel vuelve a estar disponible): suelta la referencia ANTES de
   *   `dismiss()`, así que aquí `emptyModalRef !== ref` y la superficie NO se
   *   reabre. Es lo que hace que `EMPTY → NORMAL` cierre definitivamente.
   *
   * - Cierre EXTERNO (`NgbModal.dismissAll()` de otra feature —por ejemplo el
   *   `closeAll()` del temporizador de inactividad—, o un `dismiss` inesperado):
   *   la referencia sigue siendo la nuestra. Si la condición física continúa
   *   siendo `empty`, el kiosco DEBE seguir bloqueado, así que la superficie se
   *   restaura. Sin esto quedaría `condition === 'empty'` con el bloqueo cerrado
   *   y el kiosco operable sin papel.
   *
   * No hay polling ni modificación global de `NgbModal`: la reapertura se dispara
   * desde la propia promesa `result` del modal cerrado. Tampoco hay bucle: si
   * `open()` falla, `emptyModalRef` queda en `null` y no se crea otra promesa.
   */
  private onEmptyBlockerClosed(ref: NgbModalRef): void {
    if (this.emptyModalRef !== ref) {
      return;
    }
    this.emptyModalRef = null;

    if (this.condition === 'empty') {
      this.openEmptyBlocker();
    }
  }

  // ─── Telemetría ──────────────────────────────────────────────────────────────

  /**
   * Métrica por TRANSICIÓN física del hardware, no por presentación: volver a
   * mostrar el aviso al regresar a Home no emite una métrica nueva, y los códigos
   * duplicados tampoco. Un fallo de New Relic no altera estado ni superficies.
   */
  private trackTransition(condition: BoardingPassPaperCondition): void {
    if (condition === 'normal') {
      return;
    }
    try {
      const { event, statusCode } = CONDITION_TELEMETRY[condition];
      this.newRelicEvents.trackEvent(event, {
        device: DEVICE.ATB_PRINTER,
        status_code: statusCode,
      });
    } catch {
      /* Best-effort: sin reintentos, colas ni persistencia. */
    }
  }

  // ─── Utilidades internas ─────────────────────────────────────────────────────

  /**
   * Se suscribe a `NgbModal.activeInstances` para reintentar la presentación en
   * cuanto la pila de modales quede vacía. Es la API disponible en la versión
   * instalada; evita cualquier polling.
   */
  private watchActiveInstances(): void {
    if (this.activeInstancesSub) {
      return;
    }
    this.activeInstancesSub = this.ngbModal.activeInstances.subscribe((instances) => {
      if (instances.length === 0) {
        this.presentLowIfPossible();
      }
    });
  }

  /**
   * Pide a la pantalla visible que retire sus capas de carga. Un fallo suyo nunca
   * debe impedir que el kiosco quede bloqueado.
   */
  private releaseHostLoadingLayers(): void {
    try {
      this.releaseHostLoading?.();
    } catch {
      /* El bloqueo es prioritario: se continúa igual. */
    }
  }

  private teardownActiveInstancesWatch(): void {
    this.activeInstancesSub?.unsubscribe();
    this.activeInstancesSub = null;
  }

  /** Resuelve el device ATB Printer; `null` cuando no hay dispositivo. */
  private getDevice(): AtbPrinterDeviceLike | null {
    try {
      const device = this.cussService.getDeviceManager()?.getDevice(deviceIds.ATB_PRINTER);
      return (device as unknown as AtbPrinterDeviceLike) ?? null;
    } catch {
      return null;
    }
  }
}
